import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";
import { ArrowRight } from "@phosphor-icons/react";

export function Hero() {
    const { t } = useTranslation();
    return (
        <section
            className="relative min-h-screen flex items-center justify-center px-6 pt-80 pb-16 rem-watercolor-bg"
            data-testid="hero-section"
        >
            {/* soft floating petals decor */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-[15%] left-[8%] opacity-40 animate-float-gentle">
                    <ForgetMeNotIcon size={48} />
                </div>
                <div className="absolute top-[28%] right-[12%] opacity-30 animate-float-gentle" style={{ animationDelay: "1.5s" }}>
                    <ForgetMeNotIcon size={32} />
                </div>
                <div className="absolute bottom-[18%] left-[18%] opacity-25 animate-float-gentle" style={{ animationDelay: "2.8s" }}>
                    <ForgetMeNotIcon size={36} />
                </div>
                <div className="absolute bottom-[22%] right-[10%] opacity-40 animate-float-gentle" style={{ animationDelay: "0.8s" }}>
                    <ForgetMeNotIcon size={56} />
                </div>
            </div>

            <div className="relative z-10 max-w-5xl mx-auto text-center">
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-8"
                    data-testid="hero-eyebrow"
                >
                    {t("hero.eyebrow")}
                </motion.p>

                <motion.h1
                    initial={{ opacity: 0, y: 22 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.0, delay: 0.25 }}
                    className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2B2A] leading-[1.06] tracking-tight"
                    data-testid="hero-headline"
                >
                    {t("hero.headline")}
                </motion.h1>                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.55 }}
                    className="mt-7 max-w-2xl mx-auto text-lg sm:text-xl text-[#5C5A59] font-light leading-relaxed"
                    data-testid="hero-subhead"
                >
                    {t("hero.subhead_part1")}
                    <span className="block mt-3 italic text-[#6B7DB8]">{t("hero.subhead_tagline")}</span>
                </motion.p>

                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.75 }}
                    className="mt-5 max-w-2xl mx-auto text-base sm:text-lg text-[#2D2B2A] font-serif italic leading-relaxed"
                    data-testid="hero-couples-line"
                >
                    {t("hero.couples_line")}
                </motion.p>

                {/* Recurring brand motif: "Good morning [Name], let's reminisce" greeting card */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.94, y: 28 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 1.1, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-14 mx-auto max-w-md rem-greeting-card rounded-[2rem] p-8 sm:p-10"
                    data-testid="hero-greeting-card"
                >
                    <div className="flex justify-center mb-5">
                        <ForgetMeNotIcon size={44} />
                    </div>
                    <p className="font-serif text-2xl text-[#2D2B2A] leading-snug">{t("hero.greeting_morning")}</p>
                    <p className="font-serif text-4xl text-[#4A5A95] italic mt-1 leading-tight">{t("hero.greeting_invite")}</p>
                    <Link
                        to="/sign-up"
                        className="rem-start-button inline-flex items-center gap-2 mt-8 px-9 py-3.5 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white font-medium text-base"
                        data-testid="hero-start-button"
                    >
                        {t("hero.cta_start")}
                        <ArrowRight weight="bold" size={18} />
                    </Link>
                    <div className="mt-5">
                        <Link
                            to="/demo/john"
                            className="text-xs uppercase tracking-[0.22em] text-[#6B7DB8] hover:text-[#4A5A95] font-semibold border-b border-[#6B7DB8]/30 hover:border-[#4A5A95] pb-0.5 transition-colors"
                            data-testid="hero-watch-john"
                        >
                            {t("hero.cta_watch_demo")}
                        </Link>
                    </div>
                </motion.div>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.0, delay: 1.4 }}
                    className="mt-10 text-sm text-[#8A8786]"
                    data-testid="hero-footnote"
                >
                    {t("hero.footnote")}
                </motion.p>
            </div>
        </section>
    );
}
